import { reduceWithFunction } from "./core";
import { INIT, RESULT, STEP, VALUE } from "./propertyNames";
import { ensureReduced, isReduced, reduced, unreduced } from "./util";

function updateValue(result, newValue) {
  if (isReduced(newValue)) {
    result.value = newValue[VALUE];
    return reduced(result);
  } else {
    result.value = newValue;
    return result;
  }
}

var Dedupe =
/**@__PURE__*/
function () {
  function Dedupe(xf) {
    this.xf = xf;
  }

  Dedupe.prototype[INIT] = function () {
    return {
      last: {},
      value: this.xf[INIT]()
    };
  };

  Dedupe.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result.value);
  };

  Dedupe.prototype[STEP] = function (result, input) {
    if (input !== result.last) {
      result.last = input;
      return updateValue(result, this.xf[STEP](result.value, input));
    } else {
      return result;
    }
  };

  return Dedupe;
}();

export function dedupe() {
  return function (xf) {
    return new Dedupe(xf);
  };
}

var Drop =
/**@__PURE__*/
function () {
  function Drop(xf, n) {
    this.xf = xf;
    this.n = n;
    this.i = 0;
  }

  Drop.prototype[INIT] = function () {
    return this.xf[INIT]();
  };

  Drop.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result);
  };

  Drop.prototype[STEP] = function (result, input) {
    return this.i++ < this.n ? result : this.xf[STEP](result, input);
  };

  return Drop;
}();

export function drop(n) {
  return function (xf) {
    return new Drop(xf, n);
  };
}

var DropWhile =
/**@__PURE__*/
function () {
  function DropWhile(xf, pred) {
    this.xf = xf;
    this.pred = pred;
  }

  DropWhile.prototype[INIT] = function () {
    return {
      value: this.xf[INIT](),
      isDoneDropping: false
    };
  };

  DropWhile.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result.value);
  };

  DropWhile.prototype[STEP] = function (result, input) {
    if (result.isDoneDropping) {
      return updateValue(result, this.xf[STEP](result.value, input));
    } else {
      if (this.pred(input)) {
        return result;
      } else {
        result.isDoneDropping = true;
        return updateValue(result, this.xf[STEP](result.value, input));
      }
    }
  };

  return DropWhile;
}();

export function dropWhile(pred) {
  return function (xf) {
    return new DropWhile(xf, pred);
  };
}

var Filter =
/**@__PURE__*/
function () {
  function Filter(xf, pred) {
    this.xf = xf;
    this.pred = pred;
  }

  Filter.prototype[INIT] = function () {
    return this.xf[INIT]();
  };

  Filter.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result);
  };

  Filter.prototype[STEP] = function (result, input) {
    return this.pred(input) ? this.xf[STEP](result, input) : result;
  };

  return Filter;
}();

export function filter(pred) {
  return function (xf) {
    return new Filter(xf, pred);
  };
}

var Flatten =
/**@__PURE__*/
function () {
  function Flatten(xf) {
    this.xf = xf;
    this.step = xf[STEP].bind(xf);
  }

  Flatten.prototype[INIT] = function () {
    return this.xf[INIT]();
  };

  Flatten.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result);
  };

  Flatten.prototype[STEP] = function (result, input) {
    return reduceWithFunction(input, this.step, result);
  };

  return Flatten;
}();

export function flatten() {
  return function (xf) {
    return new Flatten(xf);
  };
}
export function flatMap(f) {
  return function (xf) {
    return new MapTransformer(new Flatten(xf), f);
  };
}

var Interpose =
/**@__PURE__*/
function () {
  function Interpose(xf, separator) {
    this.xf = xf;
    this.separator = separator;
    this.isStarted = false;
  }

  Interpose.prototype[INIT] = function () {
    return this.xf[INIT]();
  };

  Interpose.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result);
  };

  Interpose.prototype[STEP] = function (result, input) {
    if (this.isStarted) {
      var withSeparator = this.xf[STEP](result, this.separator);

      if (isReduced(withSeparator)) {
        return withSeparator;
      } else {
        return this.xf[STEP](withSeparator, input);
      }
    } else {
      this.isStarted = true;
      return this.xf[STEP](result, input);
    }
  };

  return Interpose;
}();

export function interpose(separator) {
  return function (xf) {
    return new Interpose(xf, separator);
  };
} // Not named Map to avoid confusion with the native Map class.

var MapTransformer =
/**@__PURE__*/
function () {
  function MapTransformer(xf, f) {
    this.xf = xf;
    this.f = f;
  }

  MapTransformer.prototype[INIT] = function () {
    return this.xf[INIT]();
  };

  MapTransformer.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result);
  };

  MapTransformer.prototype[STEP] = function (result, input) {
    return this.xf[STEP](result, this.f(input));
  };

  return MapTransformer;
}();

export function map(f) {
  return function (xf) {
    return new MapTransformer(xf, f);
  };
}

var MapIndexed =
/**@__PURE__*/
function () {
  function MapIndexed(xf, f) {
    this.xf = xf;
    this.f = f;
  }

  MapIndexed.prototype[INIT] = function () {
    return {
      value: this.xf[INIT](),
      i: 0
    };
  };

  MapIndexed.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result.value);
  };

  MapIndexed.prototype[STEP] = function (result, input) {
    return updateValue(result, this.xf[STEP](result.value, this.f(input, result.i++)));
  };

  return MapIndexed;
}();

export function mapIndexed(f) {
  return function (xf) {
    return new MapIndexed(xf, f);
  };
}

var PartitionAll =
/**@__PURE__*/
function () {
  function PartitionAll(xf, n) {
    this.xf = xf;
    this.n = n;
    this.buffer = [];
  }

  PartitionAll.prototype[INIT] = function () {
    return this.xf[INIT]();
  };

  PartitionAll.prototype[RESULT] = function (result) {
    if (this.buffer.length > 0) {
      result = unreduced(this.xf[STEP](result, this.buffer));
      this.buffer = [];
    }

    return this.xf[RESULT](result);
  };

  PartitionAll.prototype[STEP] = function (result, input) {
    this.buffer.push(input);

    if (this.buffer.length === this.n) {
      var newResult = this.xf[STEP](result, this.buffer);
      this.buffer = [];
      return newResult;
    } else {
      return result;
    }
  };

  return PartitionAll;
}();

export function partitionAll(n) {
  if (n === 0) {
    throw new Error("Size in partitionAll() cannot be 0");
  } else if (n < 0) {
    throw new Error("Size in partitionAll() cannot be negative");
  }

  return function (xf) {
    return new PartitionAll(xf, n);
  };
}
var INITIAL_LAST_KEY = {};

var PartitionBy =
/**@__PURE__*/
function () {
  function PartitionBy(xf, f) {
    this.xf = xf;
    this.f = f;
  }

  PartitionBy.prototype[INIT] = function () {
    return {
      value: this.xf[INIT](),
      buffer: [],
      lastKey: INITIAL_LAST_KEY
    };
  };

  PartitionBy.prototype[RESULT] = function (result) {
    if (result.buffer.length > 0) {
      result.value = unreduced(this.xf[STEP](result.value, result.buffer));
      result.buffer = [];
    }

    return this.xf[RESULT](result.value);
  };

  PartitionBy.prototype[STEP] = function (result, input) {
    var key = this.f(input);
    var value = result.value,
        buffer = result.buffer,
        lastKey = result.lastKey;
    result.lastKey = key;
    var newResult;

    if (lastKey === INITIAL_LAST_KEY || lastKey === key) {
      newResult = result;
    } else {
      newResult = updateValue(result, this.xf[STEP](value, buffer));
      unreduced(newResult).buffer = [];
    }

    unreduced(newResult).buffer.push(input);
    return newResult;
  };

  return PartitionBy;
}();

export function partitionBy(f) {
  return function (xf) {
    return new PartitionBy(xf, f);
  };
}

var Take =
/**@__PURE__*/
function () {
  function Take(xf, n) {
    this.xf = xf;
    this.n = n;
    this.i = 0;
  }

  Take.prototype[INIT] = function () {
    return this.xf[INIT]();
  };

  Take.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result);
  };

  Take.prototype[STEP] = function (result, input) {
    // Written this way to avoid pulling one more element than necessary.
    if (this.n <= 0) {
      return reduced(result);
    }

    var next = this.xf[STEP](result, input);
    return this.i++ < this.n - 1 ? next : ensureReduced(next);
  };

  return Take;
}();

export function remove(pred) {
  return filter(function (item) {
    return !pred(item);
  });
}
export function take(n) {
  return function (xf) {
    return new Take(xf, n);
  };
}

var TakeNth =
/**@__PURE__*/
function () {
  function TakeNth(xf, n) {
    this.xf = xf;
    this.n = n;
  }

  TakeNth.prototype[INIT] = function () {
    return {
      value: this.xf[INIT](),
      i: 0
    };
  };

  TakeNth.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result.value);
  };

  TakeNth.prototype[STEP] = function (result, input) {
    return result.i++ % this.n === 0 ? updateValue(result, this.xf[STEP](result.value, input)) : result;
  };

  return TakeNth;
}();

export function takeNth(n) {
  if (n === 0) {
    throw new Error("Step in takeNth() cannot be 0");
  } else if (n < 0) {
    throw new Error("Step in takeNth() cannot be negative");
  }

  return function (xf) {
    return new TakeNth(xf, n);
  };
}

var TakeWhile =
/**@__PURE__*/
function () {
  function TakeWhile(xf, pred) {
    this.xf = xf;
    this.pred = pred;
  }

  TakeWhile.prototype[INIT] = function () {
    return this.xf[INIT]();
  };

  TakeWhile.prototype[RESULT] = function (result) {
    return this.xf[RESULT](result);
  };

  TakeWhile.prototype[STEP] = function (result, input) {
    return this.pred(input) ? this.xf[STEP](result, input) : reduced(result);
  };

  return TakeWhile;
}();

export function takeWhile(pred) {
  return function (xf) {
    return new TakeWhile(xf, pred);
  };
}